/**
 * Client-side design fingerprint for the buffer↔baseline dirty model (FXC-9009, buffer-lineage).
 *
 * XFOIL has a CURRENT airfoil (analysed) and a BUFFER (the scratch copy you reshape in GDES); you
 * edit the buffer freely, then explicitly promote it (PANE/EXEC) to CURRENT. The high-lift workbench
 * mirrors that: the live `config` is the BUFFER; a saved/loaded/committed geometry is the BASELINE.
 * To tell whether the buffer has diverged ("dirty") and to label the buffer with a stable identity,
 * we need a deterministic fingerprint of the *design* (the geometry the solve path keys on), NOT of
 * transient operating-point/UI state.
 *
 * This mirrors the server's {@link flatten_design} vocabulary (see
 * `thread-api/app/services/rans_provenance.py`): the SAME flat-axis keys (`cove_*` / `vane_*` /
 * `flaperon_*` / `disk_*`, indexed `element_{j}_*` / `disk_{k}_*` beyond the default triple/single),
 * so the fingerprint changes exactly when (and only when) the server-side `design_hash` would. The
 * fingerprint itself is a synchronous FNV-1a hash over the canonical (sorted-key) JSON — distinct
 * from the server's SHA1 `design_hash`, but driven by the identical flattened axes, so it is a
 * faithful DISPLAY identity + a precise dirty detector. The authoritative provenance `design_hash`
 * is the server's, returned on commit (see `SavedGeometry.design_hash`).
 *
 * Operation (deploy/flaperon/alpha/etc.) is deliberately EXCLUDED: reshaping the foil (dragging
 * control points) changes the design fingerprint; nudging the operating point does not — exactly
 * the XFOIL buffer semantics (the buffer is the *shape*).
 */

import type { HighLiftAirfoil } from './estolConfig';
import { mainCoordsDigest } from './estolConfig';
import { elementsOf, blowingOf } from './elements';

function isFiniteNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function pair(value: unknown): [number | null, number | null] {
  if (Array.isArray(value) && value.length >= 2) {
    const x = isFiniteNum(value[0]) ? value[0] : null;
    const z = isFiniteNum(value[1]) ? value[1] : null;
    return [x, z];
  }
  return [null, null];
}

/**
 * Flatten a {@link HighLiftAirfoil} into the named design-axis scalars the server keys on. Mirrors
 * the backend `flatten_design`: first-of-each-kind element → canonical legacy keys (byte-identical
 * for the DEFAULT section); EXTRA elements/blowing sources → stable indexed `element_{j}_*` /
 * `disk_{k}_*` keys. Only finite numeric values are emitted.
 */
export function flattenDesign(config: HighLiftAirfoil): Record<string, number> {
  const out: Record<string, number> = {};
  const put = (name: string, value: unknown): void => {
    if (isFiniteNum(value)) out[name] = value;
  };
  const put2 = (xKey: string, zKey: string, value: unknown): void => {
    const [x, z] = pair(value);
    if (x !== null) out[xKey] = x;
    if (z !== null) out[zKey] = z;
  };

  put('chord_m', config.main.chord);
  put('blunt_thickness', config.main.bluntThickness);
  // Base-foil IDENTITY: a digest of the main element's coords so that swapping the base foil (the
  // "Replace foil" op) moves the fingerprint and flips `isDirty`. Without it the scalar axes alone
  // are blind to the foil swap, so two different base foils collided to one hash (provenance bug).
  // The server folds the same coords into `design_hash` via `design_coords_signature`.
  put('main_foil_digest', mainCoordsDigest(config.main.coords));

  const els = elementsOf(config);
  const seen = new Set<string>();
  els.forEach((el, j) => {
    const isCanonical =
      (el.kind === 'main-cove' || el.kind === 'vane-bspline' || el.kind === 'flaperon-derived') &&
      !seen.has(el.kind);
    if (isCanonical) {
      seen.add(el.kind);
      if (el.kind === 'main-cove') {
        put('cove_upper_cut_x', el.cove.upperCutX);
        put('cove_lower_cut_x', el.cove.lowerCutX);
        put('cove_back_x', el.cove.coveX);
        // Droop nose / variable-camber LE (FXC-9009 LE-device tranche). Absent / 0 ⇒ NOT emitted ⇒
        // the default config flattens identically, preserving design_hash / golden invariants.
        if (isFiniteNum(el.droopDeg) && el.droopDeg !== 0) {
          put('droop_hinge_x', el.droopHingeX);
          put('droop_deg', el.droopDeg);
        }
      } else if (el.kind === 'vane-bspline') {
        put2('vane_te_x', 'vane_te_z', el.shape.te);
        el.shape.points.forEach((p, i) => put2(`vane_p${i + 1}_x`, `vane_p${i + 1}_z`, p));
      } else if (el.kind === 'flaperon-derived') {
        put('flaperon_upper_cut_x', el.shape.upperCutX);
        put('flaperon_lower_cut_x', el.shape.lowerCutX);
        put('flaperon_nose_tangent_upper', el.shape.noseTangentUpper);
        put('flaperon_nose_tangent_lower', el.shape.noseTangentLower);
        put2('flaperon_nose_tip_x', 'flaperon_nose_tip_z', el.shape.noseTip);
        // FRISE aileron variant (control-surface tranche). Emitted ONLY when ON so a plain flaperon
        // flattens identically — design_hash / golden invariants preserved for the default config.
        if (el.frise) {
          put('flaperon_frise', 1);
          put('flaperon_frise_horn_fraction', el.friseHornFraction ?? 0.03);
        }
      }
    } else if (el.kind === 'plain-flap') {
      // Plain flap (aileron): a control-surface element with its own hinge + deflection + cove +
      // aft-nose shape. Emit them under stable indexed `element_{j}_*` keys so a flap reshape /
      // re-deflection moves the design hash (the union member would otherwise be dropped). The
      // deflection is part of the DESIGN here (per-element, DECISION-a), unlike the Operation-driven
      // legacy deploy/flaperon angles which are excluded from the fingerprint.
      put(`element_${j}_hinge_x`, el.hingeX);
      put(`element_${j}_hinge_y`, el.hingeY);
      put(`element_${j}_deflection_deg`, el.deflectionDeg);
      put(`element_${j}_cove_upper_cut_x`, el.cove.upperCutX);
      put(`element_${j}_cove_lower_cut_x`, el.cove.lowerCutX);
      put(`element_${j}_cove_back_x`, el.cove.coveX);
      put(`element_${j}_upper_cut_x`, el.shape.upperCutX);
      put(`element_${j}_lower_cut_x`, el.shape.lowerCutX);
      put(`element_${j}_nose_tangent_upper`, el.shape.noseTangentUpper);
      put(`element_${j}_nose_tangent_lower`, el.shape.noseTangentLower);
      put2(`element_${j}_nose_tip_x`, `element_${j}_nose_tip_z`, el.shape.noseTip);
      // FRISE variant — emitted only when ON (a plain flap flattens identically when off).
      if (el.frise) {
        put(`element_${j}_frise`, 1);
        put(`element_${j}_frise_horn_fraction`, el.friseHornFraction ?? 0.03);
      }
      // HINGE-CONCENTRIC nose — emitted only when ON (the concentric arc nose + cove is a distinct
      // geometry; without this a concentric flap would hash identically to a B-spline-nose one of the
      // same params). Off ⇒ absent ⇒ the default plain flap flattens byte-identically.
      if (el.concentricNose) {
        put(`element_${j}_concentric_nose`, 1);
        if (el.cove.arc) {
          put2(`element_${j}_cove_arc_hinge_x`, `element_${j}_cove_arc_hinge_z`, el.cove.arc.hinge);
          put(`element_${j}_cove_arc_r_lo`, el.cove.arc.rLo);
          put(`element_${j}_cove_arc_r_up`, el.cove.arc.rUp);
          put(`element_${j}_cove_arc_gap`, el.cove.arc.gap);
        }
      }
    } else if (el.kind === 'plain-slat') {
      // Leading-edge slat (DEPRECATED crude kind): hinge + deflection + the leading-edge cut.
      put(`element_${j}_cut_x`, el.cutX);
      put(`element_${j}_hinge_x`, el.hingeX);
      put(`element_${j}_hinge_y`, el.hingeY);
      put(`element_${j}_deflection_deg`, el.deflectionDeg);
    } else if (el.kind === 'slat') {
      // PROPER slat (FXC-9009 LE-device tranche): cut + hinge + deflection + the rigged slot params +
      // the variant (encoded 0=slotted / 1=sealed / 2=fixed-slot). Indexed `element_{j}_*` keys so a
      // slat reshape / re-rig moves the design hash. NOTE: the server-side flatten_design
      // (rans_provenance.py) must mirror these indexed keys for the design_hash to match.
      put(`element_${j}_cut_x`, el.cutX);
      put(`element_${j}_hinge_x`, el.hingeX);
      put(`element_${j}_hinge_y`, el.hingeY);
      put(`element_${j}_deflection_deg`, el.deflectionDeg);
      put(`element_${j}_gap`, el.gap);
      put(`element_${j}_overlap`, el.overlap);
      put(`element_${j}_slot_depth`, el.slotDepth);
      put(`element_${j}_te_thickness`, el.teThickness);
      put(`element_${j}_variant`, el.variant === 'slotted' ? 0 : el.variant === 'sealed' ? 1 : 2);
    } else if (el.kind === 'krueger') {
      // Krueger flap (FXC-9009 LE-device tranche, REBUILT): lower hinge + nose cut + deflection +
      // legacy bullnose bool, PLUS the rebuilt panel/leaf params + slot rig + the encoded variant
      // (slotted=0 / sealed=1, two-state per D8). New scalars are emitted ONLY when present (put() drops
      // non-finite), so a design with no Krueger flattens identically — design_hash invariant preserved.
      // NOTE: the server-side flatten_design (rans_provenance.py) MUST mirror these keys + the variant
      // encoding identically, or client/server design hashes diverge for any Krueger config (§7.5).
      put(`element_${j}_lower_hinge_x`, el.lowerHingeX);
      put(`element_${j}_hinge_y`, el.hingeY);
      put(`element_${j}_nose_cut_x`, el.noseCutX);
      put(`element_${j}_deflection_deg`, el.deflectionDeg);
      put(`element_${j}_bullnose`, el.bullnose ? 1 : 0);
      put(`element_${j}_panel_arc_fraction`, el.panelArcFraction);
      put(`element_${j}_panel_camber`, el.panelCamber);
      put(`element_${j}_bullnose_radius`, el.bullnoseRadius);
      put(`element_${j}_panel_thickness`, el.panelThickness);
      put(`element_${j}_gap`, el.gap);
      put(`element_${j}_overlap`, el.overlap);
      put(`element_${j}_variant`, el.variant === 'slotted' ? 0 : 1);
    } else {
      // Extra / non-default vane/main element — indexed by position (stable under add/move, never
      // collides with the canonical names). Mirrors the server's `element_{j}_*` scheme.
      const shape: Record<string, unknown> =
        el.kind === 'main-cove'
          ? (el.cove as unknown as Record<string, unknown>)
          : el.kind === 'vane-bspline' || el.kind === 'flaperon-derived'
            ? (el.shape as unknown as Record<string, unknown>)
            : {};
      for (const key of Object.keys(shape).sort()) {
        const value = shape[key];
        if (key === 'points' && Array.isArray(value)) {
          value.forEach((p, i) => put2(`element_${j}_${key}_${i + 1}_x`, `element_${j}_${key}_${i + 1}_z`, p));
        } else if (Array.isArray(value)) {
          put2(`element_${j}_${key}_x`, `element_${j}_${key}_z`, value);
        } else {
          put(`element_${j}_${key}`, value);
        }
      }
    }
  });

  put2('deploy_hinge_x', 'deploy_hinge_z', config.design.deployHinge?.pivot);
  put2('flaperon_hinge_x', 'flaperon_hinge_z', config.design.flaperonHinge?.pivot);

  const track = config.design.deployTrack;
  if (track) put2('deploy_track_pivot_x', 'deploy_track_pivot_z', track.pivot);
  const motion = config.design.motion;
  if (motion) {
    put('deploy_track_travel', motion.trackTravel);
    put('motion_translation_exponent', motion.translationExponent);
  }
  // Vane follower linkage (FXC-9009): emitted ONLY when present, so the default config (no
  // follower) keeps its axes + design_hash byte-identical and its cross-project cache valid; a
  // follower-enabled config adds these two axes and hashes distinctly (correct — different mechanism).
  const follower = config.design.vaneFollower;
  if (follower) {
    put('vane_follower_gain', follower.gain);
    put('vane_follower_lag', follower.lagChords);
  }

  const sources = blowingOf(config);
  sources.forEach((src, k) => {
    const prefix = k === 0 ? 'disk' : `disk_${k}`;
    put(`${prefix}_x`, src.x);
    put(`${prefix}_z`, src.z);
    put(`${prefix}_diameter`, src.diameter);
    put(`${prefix}_tilt_deg`, src.tiltDeg);
  });

  return out;
}

/** Canonical (sorted-key) JSON of a flat scalar record — the stable hash input. */
function canonicalJson(axes: Record<string, number>): string {
  const keys = Object.keys(axes).sort();
  return JSON.stringify(keys.map((k) => [k, axes[k]]));
}

/**
 * Stable 8-hex DISPLAY fingerprint of a config's design (FNV-1a over the canonical flattened axes).
 * Equal iff {@link flattenDesign} is equal, so two configs share a fingerprint exactly when their
 * server-side `design_hash` would match. Used for the buffer's shown identity + dirty comparison;
 * the authoritative provenance hash is the server's `design_hash`.
 */
export function designFingerprint(config: HighLiftAirfoil): string {
  const json = canonicalJson(flattenDesign(config));
  // FNV-1a 32-bit (synchronous; SubtleCrypto is async and awkward in a memo).
  let h = 0x811c9dc5;
  for (let i = 0; i < json.length; i++) {
    h ^= json.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/** True when two configs have the SAME design fingerprint (the buffer has NOT diverged from baseline). */
export function sameDesign(a: HighLiftAirfoil, b: HighLiftAirfoil): boolean {
  return designFingerprint(a) === designFingerprint(b);
}
